import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import Icon from 'common/components/Icon';
import Button from 'common/components/Button';
import colors from 'common/styles/colors';

import Typography from 'common/components/Typography';
import classNames from 'classnames';
import RewardDetailsModal from '../RewardDetailsModal';
import { actions, selectors } from '../../index';
import styles from './index.module.css';

const RewardScheduleItem = ({ item }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const rewardDetails = useSelector(selectors.rewardDetails);
  const isFetchingDetails = useSelector(selectors.isFetchingRewardDetails);

  const isUnlocked = item.status === 'unlocked';
  const progressColor = isUnlocked
    ? colors['color-green-500']
    : colors['color-brand-500'];
  const statusBadgeColor = isUnlocked ? 'color-green-100' : 'color-neutral-100';
  const statusTextColor = isUnlocked ? 'color-green-900' : 'color-neutral-900';
  const statusText = isUnlocked ? 'Unlocked' : `${item.daysLeft} days left`;

  useEffect(() => {
    if (isModalOpen && item.unlockingDate) {
      dispatch(actions.rewardDetailsRequest(item.unlockingDate));
    }
  }, [isModalOpen, item.unlockingDate, dispatch]);

  const handleDetailsClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const settlementData = rewardDetails?.rewards || [];

  return (
    <>
      <div className={styles.root}>
        <div className={styles.content}>
          <div className={styles.header}>
            <div
              className={styles.statusBadge}
              style={{ backgroundColor: colors[statusBadgeColor] }}
            >
              <Icon.Lock2
                className={styles.statusIcon}
                description="Lock status"
                size="xSmall"
                fill={
                  isUnlocked
                    ? colors['color-green-900']
                    : colors['color-neutral-900']
                }
              />
              <Typography size="xs" weight="semiBold" color={statusTextColor}>
                {statusText}
              </Typography>
            </div>
            <div className={styles.detailsButtonContainer}>
              <Button
                kind="secondary"
                size="small"
                onClick={handleDetailsClick}
              >
                <button type="button">
                  <Icon.Plus2
                    className={styles.detailsIcon}
                    description="Details"
                    size="xSmall"
                    fill={colors['color-neutral-900']}
                  />
                  Details
                </button>
              </Button>
            </div>
          </div>
          <div className={styles.amountSection}>
            <Typography size="lg" weight="semiBold" color="color-neutral-900">
              {item.amount}
            </Typography>
          </div>
          <Typography size="xs" color="color-neutral-500">
            {item.period}
          </Typography>
          <div className={styles.progressSection}>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{
                  width: `${item.progress * 100}%`,
                  backgroundColor: progressColor,
                }}
              />
            </div>
            <div className={styles.dateSection}>
              <div className={styles.dateItem}>
                <Icon.Lock2
                  className={classNames(styles.statusIcon, styles.lockIcon)}
                  description="Lock start"
                  size="xxSmall"
                />
                <Typography size="xs" color="color-neutral-400">
                  {item.startDate}
                </Typography>
              </div>
              <div className={styles.dateItem}>
                <Icon.Lock2OpenedDuotone
                  className={classNames(styles.statusIcon, styles.unlockIcon)}
                  description="Unlock date"
                  size="xxSmall"
                />
                <Typography size="xs" color="color-neutral-400">
                  {item.unlockDate}
                </Typography>
              </div>
            </div>
          </div>
        </div>
        <RewardDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          rewardItem={item}
          settlementData={settlementData}
          isLoading={isFetchingDetails}
        />
      </div>
    </>
  );
};

RewardScheduleItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
    period: PropTypes.string.isRequired,
    daysLeft: PropTypes.number.isRequired,
    status: PropTypes.oneOf(['locked', 'unlocked']).isRequired,
    startDate: PropTypes.string.isRequired,
    unlockDate: PropTypes.string.isRequired,
    progress: PropTypes.number.isRequired,
    unlockingDate: PropTypes.string.isRequired,
  }).isRequired,
};

export default RewardScheduleItem;
